import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ClipboardList, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const categories = [
  { id: "infissi", name: "Infissi e Serramenti" },
  { id: "porte", name: "Porte" },
  { id: "cucine", name: "Cucine" },
  { id: "arredamento", name: "Arredamento" },
  { id: "pergole", name: "Pergole e Gazebo" },
  { id: "parquet", name: "Parquet" },
  { id: "pavimenti", name: "Pavimenti" },
  { id: "outdoor", name: "Arredamento Outdoor" },
  { id: "altro", name: "Altro" },
];

const PreventiviPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    category: "",
    description: "",
    contact_preference: "email"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.description) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      toast.success("Richiesta preventivo inviata!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'invio");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="preventivi-success">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <CheckCircle className="text-emerald-500" size={48} />
          </div>
          <h1 className="text-2xl font-light text-white">Richiesta Inviata!</h1>
          <p className="text-white/60">
            Prepareremo il tuo preventivo personalizzato e ti contatteremo presto.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/profile")}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Vedi Preventivi
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSuccess(false);
                setFormData({ category: "", description: "", contact_preference: "email" });
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Nuovo Preventivo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="preventivi-page">
      {/* Header */}
      <div className="p-4 space-y-2 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <ClipboardList className="text-emerald-500" size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-light text-white tracking-wide">Preventivi</h1>
            <p className="text-sm text-white/60">Richiedi un preventivo gratuito</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Category */}
        <div className="space-y-2">
          <Label className="text-white/70">Categoria Prodotto *</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
            <SelectTrigger 
              className="h-12 bg-zinc-900 border-zinc-800 text-white"
              data-testid="category-select"
            >
              <SelectValue placeholder="Seleziona categoria" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.name} className="text-white hover:bg-zinc-800">
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label className="text-white/70">Descrizione della richiesta *</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descrivi cosa ti serve: dimensioni, materiali preferiti, stile desiderato, budget indicativo..."
            className="bg-zinc-900 border-zinc-800 text-white min-h-[150px] focus:border-emerald-500"
            required
            data-testid="description-textarea"
          />
          <p className="text-xs text-white/40">Più dettagli fornisci, più preciso sarà il preventivo</p>
        </div>

        {/* Contact Preference */}
        <div className="space-y-3">
          <Label className="text-white/70">Come preferisci essere contattato?</Label>
          <RadioGroup
            value={formData.contact_preference}
            onValueChange={(v) => setFormData({ ...formData, contact_preference: v })}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="email" id="email" className="border-white/30 text-emerald-500" />
              <Label htmlFor="email" className="text-white cursor-pointer">Email</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="phone" id="phone" className="border-white/30 text-emerald-500" />
              <Label htmlFor="phone" className="text-white cursor-pointer">Telefono</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="whatsapp" id="whatsapp" className="border-white/30 text-emerald-500" />
              <Label htmlFor="whatsapp" className="text-white cursor-pointer">WhatsApp</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full"
          data-testid="preventivi-submit"
        >
          {loading ? "Invio in corso..." : "Richiedi Preventivo"}
        </Button>
      </form>
    </div>
  );
};

export default PreventiviPage;
